import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/free-solid-svg-icons';
import { faPen, faPlus } from '@fortawesome/free-solid-svg-icons';
import ColumnVisibilityModal from '../../../components/ColumnVisibilityModal';
import { Link } from 'react-router-dom';
import Table from '../../../components/Table';
import Pagination from '../../../components/Pagination';
import EntradaMaterialFilterBar from '../../../components/EntradaMaterialFilterBar';
import { fetchEntradasMaterial } from '../../../services/entradaMaterialService';

const columns = [
  { Header: 'ID', accessor: 'id' },
  { Header: 'Data Entrada', accessor: 'dataEntrada', Cell: (value) => {
    if (!value) return '-';
    const date = new Date(value);
    // Ajustar para evitar problema de timezone
    const adjustedDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
    return adjustedDate.toLocaleDateString('pt-BR');
  }},
  { Header: 'Quantidade', accessor: 'quantidade' },
  { Header: 'Produto', accessor: 'produtoDescricao', Cell: (value) => value || 'N/A' },
  { Header: 'Observação', accessor: 'observacao', Cell: (value) => value || '-' },
  { Header: 'Tipo', accessor: 'tipo' },
];

const pageSizes = [5, 10, 20];

const EntradaMaterialList = () => {
  const [filters, setFilters] = useState({ 
    dataEntrada: '', 
    tipo: '' 
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(pageSizes[0]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(columns.map(col => col.accessor));
  const [entradas, setEntradas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);

  // Carregar dados
  useEffect(() => {
    const loadEntradas = async () => {
      try {
        setLoading(true);
        const data = await fetchEntradasMaterial(filters, page, pageSize);
        // A API retorna um objeto com entradas, total e informações de paginação
        setEntradas(data.entradas || []);
        setTotal(data.total || 0);
        setTotalPages(data.totalPages || 0);
        setError(null);
      } catch (err) {
        setError('Erro ao carregar entradas de material');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadEntradas();
  }, [filters, page, pageSize]);

  // Modal handlers
  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);
  const handleToggleColumn = (accessor) => {
    setVisibleColumns(prev =>
      prev.includes(accessor)
        ? prev.filter(col => col !== accessor)
        : [...prev, accessor]
    );
  };

  // Filtragem - já aplicada no service
  const filteredEntradas = Array.isArray(entradas) ? entradas : [];

  // Usar dados diretamente da API (já paginados)
  const paginatedEntradas = filteredEntradas;

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(1);
  };

  if (loading) {
    return (
      <div className="flex-1 w-full max-w-full overflow-x-hidden">
        <div className="px-2 sm:px-6">
          <div className="flex justify-center items-center h-64">
            <div className="text-gray-600">Carregando...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 w-full max-w-full overflow-x-hidden">
        <div className="px-2 sm:px-6">
          <div className="flex justify-center items-center h-64">
            <div className="text-red-600">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 w-full max-w-full overflow-x-hidden">
      <div className="px-2 sm:px-6">
        <div className="max-w-full">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Listagem de Entrada de Material</h2>
              {/* O total e a página atual vêm do <Pagination> no rodapé da lista,
                  evitando dois contadores dizendo a mesma coisa (e cortando no mobile) */}
            </div>
            <Link
              to="/estoque/entrada/create"
              className="px-4 py-2 rounded bg-green-600 text-white font-semibold shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400 flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faPlus} />
              <span>Nova Entrada</span>
            </Link>
          </div>
          
          <ColumnVisibilityModal
            columns={columns}
            visibleColumns={visibleColumns}
            onChange={handleToggleColumn}
            onClose={handleCloseModal}
            isOpen={isModalOpen}
          />
          
          <div className="max-w-full">
            <EntradaMaterialFilterBar filters={filters} onChange={handleFilterChange} />
          </div>
          
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2 max-w-full">
            <div className="order-2 sm:order-1 w-full sm:w-auto">
              <Pagination currentPage={page} totalPages={totalPages} totalItems={total || 0} itemsPerPage={pageSize} onPageChange={handlePageChange} />
            </div>
            <div className="order-1 sm:order-2 flex items-center w-full sm:w-auto justify-end gap-2">
              <label htmlFor="pageSize" className="text-sm text-gray-700 whitespace-nowrap mr-2">Por página:</label>
              <select
                id="pageSize"
                value={pageSize}
                onChange={e => handlePageSizeChange(Number(e.target.value))}
                className="px-2 py-1 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-400"
              >
                {pageSizes.map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
              <button
                type="button"
                className="flex items-center gap-2 px-3 py-1 rounded bg-green-600 text-white font-medium shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400"
                onClick={handleOpenModal}
                aria-label="Exibir/Ocultar colunas"
              >
                <FontAwesomeIcon icon={faEye} />
                <span>Colunas</span>
              </button>
            </div>
          </div>
          
          <div className="max-w-full">
            <Table
              columns={columns.filter(col => visibleColumns.includes(col.accessor)).map(col => {
                if (col.accessor === 'id') {
                  return {
                    ...col,
                    Cell: (value, row) => (
                      <div className="flex items-center gap-2">
                        <span>{value}</span>
                        <Link
                          to={`/estoque/entrada/${row.id}/edit`}
                          className="text-green-600 hover:text-green-800 p-1 rounded focus:outline-none"
                          title="Editar"
                        >
                          <FontAwesomeIcon icon={faPen} />
                        </Link>
                      </div>
                    )
                  };
                }
                return col;
              })}
              data={paginatedEntradas}
            />
          </div>
          
          {filteredEntradas.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              Nenhuma entrada de material encontrada.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EntradaMaterialList;
